import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, BookOpen, User, LogOut, Star, Library } from "lucide-react";
import { useAuth } from "../AuthContext";
import InstallPrompt from "./InstallPrompt";
import FredbyMediaBar from "./FredbyMediaBar";
import BuiltByBadge from "./BuiltByBadge";

const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { to: "/library", label: "Library", icon: BookOpen },
    { to: "/new", label: "New" },
    { to: "/about", label: "About" },
  ];

  return (
    <div className="min-h-screen bg-ff-bg text-ff-text">
      <FredbyMediaBar />
      {/* Navigation */}
      <nav
        data-testid="main-navigation"
        className={`fixed top-9 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-ff-bg/95 backdrop-blur-md border-b border-ff-border" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              data-testid="logo-link"
              className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
            >
              <img src="/freddy-logo.png" alt="Freddy Fiction" className="h-8 w-8" />
              <span className="font-serif text-xl md:text-2xl text-ff-text">Freddy Fiction</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`font-sans text-sm font-light tracking-wide transition-colors flex items-center gap-1.5 ${
                    location.pathname === link.to || location.pathname.startsWith(link.to + "/")
                      ? "text-ff-text"
                      : "text-ff-text-muted hover:text-ff-text"
                  }`}
                >
                  {link.icon && <link.icon size={15} />}
                  {link.label}
                </Link>
              ))}
              <Link
                to="/subscribe"
                data-testid="nav-subscribe"
                className="btn-primary px-6 py-2 text-sm"
              >
                Subscribe
              </Link>
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    data-testid="nav-user-menu-btn"
                    className="flex items-center gap-2 text-ff-text-muted hover:text-ff-text transition-colors text-sm"
                  >
                    <div className="w-7 h-7 rounded-full bg-ff-surface border border-ff-border flex items-center justify-center">
                      {user.tier === "patron" ? <Star size={13} className="text-amber-500" fill="currentColor" /> : <User size={13} />}
                    </div>
                    <span className="hidden lg:inline">{user.name || user.email.split("@")[0]}</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-ff-surface border border-ff-border py-2 z-50">
                      <div className="px-4 py-2 border-b border-ff-border">
                        <p className="text-ff-text text-sm truncate">{user.email}</p>
                        <p className="text-ff-text-muted text-xs capitalize">{user.tier} tier</p>
                      </div>
                      <Link
                        to="/my-library"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="nav-my-library"
                        className="w-full text-left px-4 py-2 text-sm text-ff-text-muted hover:text-ff-text hover:bg-ff-bg transition-colors flex items-center gap-2"
                      >
                        <Library size={14} /> My Library
                      </Link>
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); }}
                        data-testid="nav-logout-btn"
                        className="w-full text-left px-4 py-2 text-sm text-ff-text-muted hover:text-ff-text hover:bg-ff-bg transition-colors flex items-center gap-2"
                      >
                        <LogOut size={14} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="text-ff-text-muted hover:text-ff-text transition-colors text-sm font-light"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-ff-text"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden bg-ff-bg border-t border-ff-border"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block font-sans text-lg text-ff-text-muted hover:text-ff-text transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/subscribe"
                data-testid="mobile-nav-subscribe"
                className="block btn-primary text-center mt-6"
              >
                Subscribe
              </Link>
              {user ? (
                <>
                  <Link
                    to="/my-library"
                    data-testid="mobile-nav-my-library"
                    className="block font-sans text-lg text-ff-text-muted hover:text-ff-text transition-colors"
                  >
                    My Library
                  </Link>
                  <button
                    onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                    data-testid="mobile-nav-logout"
                    className="block text-ff-text-muted hover:text-ff-text transition-colors text-lg mt-4"
                  >
                    Sign Out ({user.name || user.email.split("@")[0]})
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="mobile-nav-login"
                  className="block text-ff-text-muted hover:text-ff-text transition-colors text-lg mt-4"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer 
        data-testid="footer"
        className="bg-ff-dark-section border-t border-ff-border"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center gap-2.5">
                <img src="/freddy-logo.png" alt="Freddy Fiction" className="h-8 w-8" />
                <span className="font-serif text-2xl text-ff-text">Freddy Fiction</span>
              </Link>
              <p className="mt-4 text-ff-text-muted text-sm font-light leading-relaxed">
                Serialized fiction for people who ask too many questions.
              </p>
            </div>

            {/* Browse */}
            <div>
              <h4 className="label text-ff-text-muted mb-4">Browse</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/library" className="text-ff-text-muted hover:text-ff-text transition-colors text-sm">
                    Full Library
                  </Link>
                </li>
                <li>
                  <Link to="/new" className="text-ff-text-muted hover:text-ff-text transition-colors text-sm">
                    New Chapters
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-ff-text-muted hover:text-ff-text transition-colors text-sm">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/subscribe" className="text-ff-text-muted hover:text-ff-text transition-colors text-sm">
                    Subscribe
                  </Link>
                </li>
              </ul>
            </div>

            {/* Featured Series */}
            <div>
              <h4 className="label text-ff-text-muted mb-4">Featured Series</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/the-lattice" className="text-ff-text-muted hover:text-ff-lattice transition-colors text-sm">
                    The Lattice
                  </Link>
                </li>
                <li>
                  <Link to="/somnium" className="text-ff-text-muted hover:text-ff-somnium transition-colors text-sm">
                    SOMNIUM
                  </Link>
                </li>
                <li>
                  <Link to="/celestial" className="text-ff-text-muted hover:text-ff-celestial transition-colors text-sm">
                    Celestial
                  </Link>
                </li>
                <li>
                  <Link to="/the-overflow" className="text-ff-text-muted hover:text-amber-500 transition-colors text-sm">
                    The Overflow
                  </Link>
                </li>
                <li>
                  <Link to="/attended" className="text-ff-text-muted hover:text-emerald-500 transition-colors text-sm">
                    ATTENDED
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="label text-ff-text-muted mb-4">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="https://freddymedia.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-ff-text-muted hover:text-ff-text transition-colors text-sm"
                  >
                    Freddy Media
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="text-ff-text-muted hover:text-ff-text transition-colors text-sm">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-ff-text-muted hover:text-ff-text transition-colors text-sm">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
              <p className="mt-4 text-ff-text-muted text-xs">
                &copy; {new Date().getFullYear()} Freddy Fiction. All rights reserved.
              </p>
            </div>
          </div>

          {/* Built by credit */}
          <div data-testid="footer-built-by" className="mt-12 pt-8 border-t border-ff-border/50 text-center">
            <p className="text-ff-text-muted text-xs">
              A project by{" "}
              <a href="https://freddymedia.com" target="_blank" rel="noopener noreferrer" className="text-ff-text hover:text-ff-accent transition-colors">Freddy Media</a>
              {" "}&{" "}
              <a href="https://freddycompute.com" target="_blank" rel="noopener noreferrer" className="text-ff-text hover:text-ff-accent transition-colors">Freddy Compute</a>
              {" "}&mdash; built entirely with AI.{" "}
              <a
                href="https://meetings.hubspot.com/jonfred/freddy-ai-roundrobin?uuid=133f5796-273b-430b-b866-3ebd46d92af4"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-book-demo"
                className="text-ff-accent hover:underline"
              >
                Want one? Book a demo.
              </a>
            </p>
          </div>
        </div>
      </footer>
      <InstallPrompt />
      <BuiltByBadge />
    </div>
  );
};

export default Layout;
